import React, { useEffect, useState } from "react";

const SudokuEditComponent = ({ game_id, date, onCancel, challenge }) => {
  const CURRENT_SELECTED_CHALLENGE = challenge;
  const [challengeDict, setChallengeDict] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!game_id || !date) return;
    setLoading(true);
    setError(null);
    const url = `${process.env.REACT_APP_CMC_API}/statistics?method=getDailyChallengeComplex&gameid=${game_id}&date=${date}`;
    fetch(url, {
      headers: { Connection: "close" },
      credentials: "include",
    })
      .then((response) => {
        if (!response.ok) throw new Error("Failed to fetch complex challenge data");
        return response.json();
      })
      .then((json) => {
        if (json && json.challenge_name && Array.isArray(json.challenge_name)) {
          const dict = {};
          for (let i = 0; i < json.challenge_name.length; i++) {
            const name = json.challenge_name[i];
            if (!dict[name]) {
              dict[name] = {
                game_id: json.game_id?.[i],
                challenge_name: name,
                variants: {},
              };
            }
            const variant_name = json.variant_name?.[i];
            const variant_id = json.variant_id?.[i];
            const challenge_data = json.challenge_data?.[i];

            if (variant_name) {
              dict[name].variants[variant_name] = {
                variant_id: variant_id,
                challenge_data: challenge_data,
              };
            }
          }
          setChallengeDict(dict);
        } else {
          setChallengeDict(null);
        }
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [game_id, date]);
  
  const selectedChallenge = challengeDict?.[CURRENT_SELECTED_CHALLENGE];
  const challengeData = selectedChallenge?.variants?.challenge?.challenge_data;

  // Parse the challenge data
  let difficulty = "";
  let puzzle = [];
  let solution = [];
  let puzzleGrid = [];
  let solutionGrid = [];

  if (challengeData) {
    try {
      // Parse the format: "difficulty-puzzle-solution"
      const parts = challengeData.split('-');
      if (parts.length >= 3) {
        difficulty = parts[0];
        const puzzleStr = parts[1];
        const solutionStr = parts[2];

        // Convert string to 9x9 grid
        if (puzzleStr.length === 81) {
          puzzle = puzzleStr.split('').map(char => char === '.' ? '' : char);
          puzzleGrid = [];
          for (let i = 0; i < 9; i++) {
            puzzleGrid[i] = puzzle.slice(i * 9, (i + 1) * 9);
          }
        }

        if (solutionStr.length === 81) {
          solution = solutionStr.split('');
          solutionGrid = [];
          for (let i = 0; i < 9; i++) {
            solutionGrid[i] = solution.slice(i * 9, (i + 1) * 9);
          }
        }
      }
    } catch (e) {
      console.error("Failed to parse challenge data:", e);
    }
  }

  const renderGrid = (grid, title, showSolution = false) => {
    if (!grid || grid.length === 0) return null;

    return (
      <div className="mb-4">
        <h4 className="font-semibold mb-2 text-gray-900 dark:text-white">{title}</h4>
        <div className="bg-gray-100 dark:bg-neutral-800 p-3 rounded-md flex justify-center">
          <div className="inline-block border-2 border-gray-300 dark:border-gray-600">
            {grid.map((row, rowIndex) => (
              <div key={rowIndex} className="flex">
                {row.map((cell, colIndex) => {
                  const isOriginal = puzzleGrid[rowIndex]?.[colIndex] !== '';
                  const cellValue = showSolution ? cell : (isOriginal ? cell : '');
                  
                  return (
                    <div
                      key={`${rowIndex}-${colIndex}`}
                      className={`
                        w-8 h-8 border border-gray-200 dark:border-gray-700 
                        flex items-center justify-center text-sm font-bold
                        ${isOriginal 
                          ? 'bg-white dark:bg-neutral-700 text-gray-900 dark:text-white' 
                          : 'bg-gray-50 dark:bg-neutral-600 text-gray-500 dark:text-gray-400'
                        }
                        ${(rowIndex + 1) % 3 === 0 && rowIndex < 8 ? 'border-b-2 border-gray-400 dark:border-gray-500' : ''}
                        ${(colIndex + 1) % 3 === 0 && colIndex < 8 ? 'border-r-2 border-gray-400 dark:border-gray-500' : ''}
                      `}
                    >
                      {cellValue}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const countEmptyCells = () => {
    if (!puzzleGrid.length) return 0;
    return puzzleGrid.flat().filter(cell => cell === '').length;
  };

  const countFilledCells = () => {
    if (!puzzleGrid.length) return 0;
    return puzzleGrid.flat().filter(cell => cell !== '').length;
  };
  
  if (loading) return <div>Loading complex challenge data...</div>;
  if (error) return <div style={{ color: 'red' }}>Error: {error}</div>;

  return (
    <div className="p-3 rounded-xl mt-2">
        {/* Challenge Info */}
        <div className="mb-4 p-3 bg-gray-50 dark:bg-neutral-800 rounded-md">
            <div className="text-sm text-gray-600 dark:text-gray-300 space-y-1">
                <div><strong>Difficulty:</strong> {difficulty ? difficulty.charAt(0).toUpperCase() + difficulty.slice(1) : 'Unknown'}</div>
                <div><strong>Given Numbers:</strong> {countFilledCells()}</div>
                <div><strong>Empty Cells:</strong> {countEmptyCells()}</div>
                <div><strong>Grid Size:</strong> 9 × 9</div>
            </div>
        </div>

        {/* Puzzle Grid */}
        {renderGrid(puzzleGrid, "Puzzle Grid")}

        {/* Solution Grid */}
        {renderGrid(solutionGrid, "Solution Grid", true)}
      
        <div className="flex justify-end gap-2 mt-4">
          <button title="Close" onClick={onCancel} className="bg-gray-50 hover:bg-gray-200 dark:bg-neutral-700 dark:hover:bg-neutral-600 p-2 rounded-md">
            <svg width="20" height="20" fill="none" stroke="#F8A5A5" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>
    </div>
  );
};

export default SudokuEditComponent; 